'use client'

import { CloseIcon } from '@/components/icons/CloseIcon'
import { SearchIcon } from '@/components/icons/SearchIcon'
import { useClickOutside } from '@/hooks/use-click-outside'
import { Link } from '@/i18n/navigation'
import { ISearchOption, ISearchProps } from '@/lib/types/search.types'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { useMemo, useRef, useState } from 'react'

export function Search({
  options = [],
  onSelect,
  onSearch,
  className = '',
  disabled = false,
  maxResults = 10,
  clearOnSelect = false,
  inputBg = 'bg-white'
}: ISearchProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const t = useTranslations('SearchData')

  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return []

    const filtered = options.filter((option) =>
      t(option.label).toLowerCase().includes(query.toLowerCase())
    )

    return filtered.slice(0, maxResults)
  }, [options, query, maxResults, t])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    setSelectedIndex(-1)
    setIsOpen(value.length > 0)

    onSearch?.(value)
  }

  const handleOptionSelect = (option: ISearchOption) => {
    onSelect?.(option)

    if (clearOnSelect) {
      setQuery('')
    } else {
      setQuery(option.label)
    }

    setIsOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.focus()
    handleClear()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredOptions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        )
        break

      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        )
        break

      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[selectedIndex])
        }
        break

      case 'Escape':
        setIsOpen(false)
        setSelectedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }

  const handleClear = () => {
    setQuery('')
    setIsOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.focus()
    onSearch?.('')
  }

  useClickOutside(containerRef, () => {
    setIsOpen(false)
    setSelectedIndex(-1)
  })

  const handleFocus = () => {
    if (query.length > 0) setIsOpen(true)
  }

  const showFillteredOptions = isOpen && filteredOptions.length > 0

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <SearchIcon
            size="16"
            className={cn(
              'transition-colors duration-200',
              disabled ? 'text-gray-400' : 'text-primary-500'
            )}
          />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={t('placeholder')}
          disabled={disabled}
          className={cn(
            'w-full rounded-3xl border border-gray-300 py-2 pr-10 pl-10',
            'text-body-2 text-gray-900 placeholder-gray-500',
            'focus:ring-primary-500 focus:border-transparent focus:ring-2 focus:outline-none',
            'transition-all duration-200',
            disabled && 'cursor-not-allowed bg-gray-100',
            'hover:border-gray-400',
            inputBg
          )}
        />

        {query && !disabled && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 transition-colors duration-200 hover:text-gray-700"
            aria-label="Clear search"
          >
            <CloseIcon size="16" className="text-gray-500" />
          </button>
        )}
      </div>

      {showFillteredOptions && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
          <ul ref={listRef} className="max-h-64 overflow-y-auto py-2">
            {filteredOptions.map((option) => (
              <li
                onClick={() => handleOptionSelect(option)}
                key={option.id}
                className={cn(
                  'text-body-2 cursor-pointer px-4 py-2',
                  'transition-colors duration-150 hover:bg-gray-50',
                  'text-primary-700'
                )}
              >
                <Link href={option.href}>{t(option.label)}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
